import { AnalyticsBrowser } from "@segment/analytics-next";
import { isEmpty } from "lib/utils/isEmpty";
import { debug, isTrackingEnabled } from "./helpers";
import { getIdentityUuid, getAnonymousUuid, getGdlContext } from "./identity";
import { getUtmData, getReferrer } from "./utm";

const SEGMENT_TOKEN = process.env.NEXT_PUBLIC_SEGMENT_TOKEN;
const SEGMENT_ENABLED = !!SEGMENT_TOKEN;

const FAKE_SEGMENT_USER = {
  id: () => {},
  anonymousId: () => {},
};

const FAKE_SEGMENT = {
  load: () => {},
  page: () => {},
  user: () => FAKE_SEGMENT_USER,
  track: () => {},
  identify: () => {},
};

export const EVENT_ACTION_TYPES = {
  open: "open",
  close: "close",
  back: "back",
};

const EVENTS_AND_ALIASES = {
  emailRoleEntrySuccess: "Email Submitted",
  newAnalyticsSession: "New Analytics Session",
  fieldChanged: "Field Changed",
  formSubmitted: "Form Submitted",
  buttonClicked: "Button Clicked",
  linkClicked: "Link Clicked",
};

export const EVENT_NAMES = {
  buttonClicked: "buttonClicked",
  linkClicked: "linkClicked",
  formSubmitted: "formSubmitted",
  fieldChanged: "fieldChanged",
};

let segmentInstance = null;
let loadPromise = null;

function buildEventContext(data = {}) {
  const out = {};
  const campaign = getUtmData();
  const referrer = getReferrer();

  // Add data to meta to view the custom data in the tracks table
  // This is also where we can pass user data (context store) to the segment context, {context overload}
  if (!isEmpty(data)) out.meta = [data];

  if (campaign) out.campaign = campaign;

  if (referrer) out.referrer = { url: referrer };

  return out;
}

// Actually deliver a track event to segment.com
export function trackEvent(eventName, data) {
  debug("segment", `track: ${eventName}`);

  return loadSegment().then(() => {
    segmentInstance.track(eventName, data, {
      context: buildEventContext(data),
      anonymousId: getAnonymousUuid(), // set to better ensure we have it - see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/identity/#override-the-default-anonymous-id-with-a-call
      id: getIdentityUuid(),
    });
  });
}

function loadSegment() {
  if (loadPromise) return loadPromise;

  debug("segment", "Loading segment");

  loadPromise = new Promise((resolve) => {
    if (SEGMENT_ENABLED && isTrackingEnabled()) {
      const apiKey = SEGMENT_TOKEN;

      const analyticsBrowser = new AnalyticsBrowser();

      // TODO: custom CDN: https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/custom-proxy/#custom-cdn--api-proxy
      analyticsBrowser
        .load({ writeKey: apiKey })
        .then(() => {
          segmentInstance = analyticsBrowser;
          attemptSegmentIdentification().then(() => resolve());
        })
        .catch((err) => {
          debug("segment", `failed to load: ${err}`);

          segmentInstance = FAKE_SEGMENT; // use fake so rest of code continues
          attemptSegmentIdentification().then(() => resolve());
        });
    } else {
      segmentInstance = FAKE_SEGMENT;
      attemptSegmentIdentification().then(() => resolve());
    }
  });

  return loadPromise;
}

async function attemptSegmentIdentification() {
  const identityUuid = getIdentityUuid();
  const anonymousUuid = getAnonymousUuid();

  const user = await segmentInstance.user();
  const currentIdentityUuid = user.id();
  const currentAnonymousUuid = user.anonymousId();

  let updates = null;

  if (currentIdentityUuid !== identityUuid) {
    updates = updates ?? {};
    updates.userId = identityUuid;
    user.id(identityUuid);
  }

  if (currentAnonymousUuid !== anonymousUuid) {
    updates = updates ?? {};
    updates.anonymousId = anonymousUuid;
    // from https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/identity/#override-the-default-anonymous-id-with-a-call
    user.anonymousId(anonymousUuid);
  }

  if (updates) {
    updates.context = buildEventContext();

    debug("segment", `Identifying: ${JSON.stringify(updates)}`);

    segmentInstance.identify(updates.userId, {}, updates);
  }

  return true;
}

export function scheduleUuidSync() {
  loadSegment().then(attemptSegmentIdentification);
}

export function syncPage() {
  /* Because of the nested routes in react router & the subsequent delayed child mounting,
   *  the path and url automatically generated by Segment are not always up to date. This ensures
   *  that the correct path and url is set on every page sync. */
  const { search, pathname } = window.location;
  const { title } = document;
  const url = `${window.location.origin}${pathname}`;

  loadSegment().then(() => {
    debug("segment", `Tracking page view: ${pathname}`);

    segmentInstance.page(
      pathname,
      {
        title,
        search,
        path: pathname,
        url,
      },
      {
        context: buildEventContext(),
        anonymousId: getAnonymousUuid(), // set to better ensure we have it - see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/identity/#override-the-default-anonymous-id-with-a-call
        gdlContext: getGdlContext(),
        id: getIdentityUuid(),
      }
    );
  });
}

export function trackNewSession() {
  trackEvent(EVENTS_AND_ALIASES.newAnalyticsSession);
}
